import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { updateServiceRequest } from '../../services/api';

export const REPORT_FIELDS = [
  { key: 'problem_found', label: 'Description du problème', placeholder: 'Ce que vous avez constaté sur place...' },
  { key: 'work_performed', label: 'Travaux effectués', placeholder: 'Tâches réalisées, tests, réglages...' },
  { key: 'materials_used', label: 'Matériel utilisé', placeholder: 'Pièces et fournitures utilisées (quantités)...' },
];

const fromRequest = (request) =>
  Object.fromEntries(REPORT_FIELDS.map(({ key }) => [key, request?.[key] || '']));

export const hasReport = (request) => REPORT_FIELDS.some(({ key }) => (request?.[key] || '').trim());

// Rapport d'intervention du technicien : trois boîtes de texte distinctes.
export const ServiceReportForm = ({ request, canEdit, onSaved }) => {
  const [form, setForm] = useState(() => fromRequest(request));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(fromRequest(request));
  }, [request]);

  const dirty = REPORT_FIELDS.some(({ key }) => form[key] !== (request?.[key] || ''));

  const handleSave = async () => {
    setSaving(true);
    try {
      const changes = Object.fromEntries(
        REPORT_FIELDS.filter(({ key }) => form[key] !== (request?.[key] || '')).map(({ key }) => [key, form[key]])
      );
      await updateServiceRequest(request.id, changes);
      toast.success("Rapport d'intervention enregistré");
      onSaved?.({ ...request, ...changes });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'enregistrement du rapport");
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit && !hasReport(request)) return null;

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-4" data-testid="service-report">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-5 h-5 text-[#00b4d8]" />
        <p className="font-semibold text-[#1a3a5c]">Rapport d'intervention</p>
        {request?.report_updated_at && (
          <span className="ml-auto text-xs text-slate-400">
            {request.report_updated_by_name} · {new Date(request.report_updated_at).toLocaleDateString('fr-CA')}
          </span>
        )}
      </div>
      {REPORT_FIELDS.map(({ key, label, placeholder }) => (
        <div key={key}>
          <Label htmlFor={`report-${key}`}>{label}</Label>
          {canEdit ? (
            <Textarea
              id={`report-${key}`}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              placeholder={placeholder}
              maxLength={4000}
              className="mt-1 min-h-[90px]"
              data-testid={`report-${key}`}
            />
          ) : (
            <p className="mt-1 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{request?.[key] || '-'}</p>
          )}
        </div>
      ))}
      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="report-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer le rapport'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ServiceReportForm;
